/**
 * Required-shot checklists — the reason the app is worth opening.
 *
 * RUS-3 §4.2: "The checklist shows what is still missing, on the roof, while the rep can
 * still walk ten feet and take it. This is the whole point of the feature." The failure
 * this attacks is the expensive one: a shot that was never taken cannot be taken later,
 * because by the time the adjuster asks, the roof has been replaced.
 *
 * This module is deliberately in `sync-core` and not in the PWA. Slot ids are stamped
 * into photo payloads and therefore into `event_log` (ADR-0001 §4.7), so the server has
 * to understand them too, and neither side gets to hold a private copy of the list.
 *
 * **Slot ids are a one-way door.** `roof.test_square` will be sitting in evidence
 * attached to a live insurance claim. Renaming one orphans every photo that carries it.
 * Add slots freely; retire them by dropping `required`, never by renaming. The label and
 * the `why` are display text and may be reworded at will.
 */

/**
 * The three capture situations from RUS-3 §4.2. Each is a different person at a different
 * moment: the rep on the inspection, the crew during tear-off, the tech on a mitigation
 * job. They are separate templates rather than one long list because a crew lead holding
 * a nail gun will not scroll past eleven roofing slots to find theirs.
 */
export type InspectionKind = 'roof' | 'tearoff' | 'mitigation';

export interface ShotSlot {
  /** Stable forever. Namespaced by kind so ids stay unique across templates. */
  readonly id: string;
  /** What the rep reads on a phone in the sun. Short, concrete, no jargon. */
  readonly label: string;
  /**
   * Why the shot matters, in money or proof terms.
   *
   * This is not decoration. A checklist that only says "Drip edge" gets skipped by
   * someone in a hurry; one that says "code item — funds a supplement line" does not.
   * It is also what we show when someone finishes with slots outstanding.
   */
  readonly why: string;
  /** Whether an inspection is incomplete without it. */
  readonly required: boolean;
  /**
   * Whether the slot expects many photos ("each slope") rather than one.
   *
   * Repeatable slots are satisfied by the first photo — we cannot know how many slopes a
   * house has — but the UI keeps offering "add another" instead of showing it as done.
   */
  readonly repeatable: boolean;
}

export interface InspectionTemplate {
  readonly kind: InspectionKind;
  readonly label: string;
  readonly slots: readonly ShotSlot[];
}

/**
 * Roof / storm inspection — RUS-3 Step 3, the most consequential step in the business.
 *
 * Ordering is walk order, not importance order: verify the address from the ground, work
 * the elevations, get on the roof, then come back inside. A checklist ordered by
 * importance makes the rep climb the ladder twice.
 */
const ROOF: InspectionTemplate = {
  kind: 'roof',
  label: 'Roof inspection',
  slots: [
    {
      id: 'roof.address_verification',
      label: 'House front, street number visible',
      why: 'Proves every later photo is of this house. Without it a whole set is arguable.',
      required: true,
      repeatable: false,
    },
    {
      id: 'roof.elevation',
      label: 'Each elevation (N/S/E/W)',
      why: 'Establishes the structure and the direction the storm came from.',
      required: true,
      repeatable: true,
    },
    {
      id: 'roof.slope_wide',
      label: 'Each slope, wide',
      why: 'Context shot. An adjuster needs to place every close-up on a specific slope.',
      required: true,
      repeatable: true,
    },
    {
      id: 'roof.test_square',
      label: 'Test square per slope, hits marked',
      why: 'Carriers evaluate on hits per 10x10 square. This is the measurement the claim turns on.',
      required: true,
      repeatable: true,
    },
    {
      id: 'roof.collateral',
      label: 'Collateral damage — soft metals, gutters, screens, AC fins',
      why: 'Proves hail was present and sized. Often what wins a contested claim.',
      required: true,
      repeatable: true,
    },
    {
      id: 'roof.ridge_hip',
      label: 'Ridge and hip',
      why: 'Separate line items in the scope. Not visible in a slope-wide shot.',
      required: true,
      repeatable: false,
    },
    {
      id: 'roof.penetrations_flashing',
      label: 'Penetrations and flashing',
      why: 'Vents, pipe boots, and step flashing are line items and common leak sources.',
      required: true,
      repeatable: true,
    },
    {
      id: 'roof.edge_layers',
      label: 'Roof edge showing layer count',
      why: 'Layer count drives tear-off cost. Unprovable once the old roof is in the dumpster.',
      required: true,
      repeatable: false,
    },
    {
      id: 'roof.decking',
      label: 'Decking condition',
      why: 'Baseline for any decking supplement discovered at tear-off.',
      required: true,
      repeatable: false,
    },
    {
      id: 'roof.ventilation',
      label: 'Ventilation',
      why: 'Code upgrade item. Frequently missed in the carrier first scope.',
      required: true,
      repeatable: false,
    },
    {
      id: 'roof.interior',
      label: 'Interior staining / attic',
      why: 'Corroborates active leaks. Not always applicable, so not required.',
      required: false,
      repeatable: true,
    },
  ],
};

/**
 * Tear-off — RUS-3 Step 10, captured by the crew, not the rep.
 *
 * Every slot here documents something that is permanently buried within hours. This is
 * the template with the shortest evidence window in the whole product.
 */
const TEAROFF: InspectionTemplate = {
  kind: 'tearoff',
  label: 'Tear-off / install',
  slots: [
    {
      id: 'tearoff.site_pre_existing',
      label: 'Site before anything is touched',
      why: 'Landscaping, driveway, gutters. This is what settles a "your crew broke it" dispute.',
      required: true,
      repeatable: true,
    },
    {
      id: 'tearoff.deck_exposed',
      label: 'Deck exposed',
      why: 'The only moment the substrate is visible.',
      required: true,
      repeatable: true,
    },
    {
      id: 'tearoff.decking_replaced',
      label: 'Each decking sheet replaced',
      why: 'Directly funds a supplement line. Unprovable once the new roof is on. Skip only if none were replaced.',
      required: false,
      repeatable: true,
    },
    {
      id: 'tearoff.ice_and_water',
      label: 'Ice-and-water shield',
      why: 'Code item and warranty proof. Covered by the next layer within the hour.',
      required: true,
      repeatable: false,
    },
    {
      id: 'tearoff.underlayment',
      label: 'Underlayment',
      why: 'Warranty proof. Buried immediately.',
      required: true,
      repeatable: false,
    },
    {
      id: 'tearoff.drip_edge',
      label: 'Drip edge',
      why: 'Code item routinely left out of the carrier first scope.',
      required: true,
      repeatable: false,
    },
    {
      id: 'tearoff.valleys_flashing',
      label: 'Valleys and flashing',
      why: 'Highest-risk leak detail. Warranty proof.',
      required: true,
      repeatable: true,
    },
    {
      id: 'tearoff.completion',
      label: 'Completed roof',
      why: 'Closeout and the before/after pair the homeowner sees.',
      required: true,
      repeatable: true,
    },
    {
      id: 'tearoff.magnet_sweep',
      label: 'Magnet sweep and debris cleared',
      why: 'A nail in a tire is a complaint and sometimes a claim.',
      required: true,
      repeatable: false,
    },
  ],
};

/** Mitigation — RUS-3 Track M. Hours-to-days clock, heavier documentation cadence. */
const MITIGATION: InspectionTemplate = {
  kind: 'mitigation',
  label: 'Mitigation',
  slots: [
    {
      id: 'mitigation.source_of_loss',
      label: 'Source of loss',
      why: 'Cause of loss decides coverage. Everything else depends on it.',
      required: true,
      repeatable: false,
    },
    {
      id: 'mitigation.affected_room',
      label: 'Each affected room',
      why: 'Affected area drives the scope. A room not photographed is a room not billed.',
      required: true,
      repeatable: true,
    },
    {
      id: 'mitigation.moisture_reading',
      label: 'Moisture reading, meter in frame',
      why: 'The reading and the meter in one shot. A number without the meter is an assertion.',
      required: true,
      repeatable: true,
    },
    {
      id: 'mitigation.equipment_placed',
      label: 'Equipment placed, with count',
      why: 'Equipment is billed per unit per day. The photo is the invoice backup.',
      required: true,
      repeatable: true,
    },
  ],
};

export const INSPECTION_TEMPLATES: Readonly<Record<InspectionKind, InspectionTemplate>> = {
  roof: ROOF,
  tearoff: TEAROFF,
  mitigation: MITIGATION,
};

export function inspectionTemplate(kind: InspectionKind): InspectionTemplate {
  return INSPECTION_TEMPLATES[kind];
}

export function isInspectionKind(value: unknown): value is InspectionKind {
  return value === 'roof' || value === 'tearoff' || value === 'mitigation';
}

/** The minimum a photo has to expose for checklist maths. Keeps this module storage-free. */
export interface SlottedPhoto {
  readonly slotId: string;
  /** Redacted evidence stays readable (ADR-0001 §4.4) but stops satisfying a slot. */
  readonly redacted?: boolean;
}

export interface SlotProgress {
  readonly slot: ShotSlot;
  /** Photos currently counting toward this slot. Excludes redacted. */
  readonly count: number;
  readonly satisfied: boolean;
}

export interface ChecklistProgress {
  readonly template: InspectionTemplate;
  readonly slots: readonly SlotProgress[];
  readonly requiredTotal: number;
  readonly requiredSatisfied: number;
  /** Required slots with nothing in them. What the rep still has to walk over and shoot. */
  readonly missing: readonly ShotSlot[];
  readonly complete: boolean;
  /**
   * Photos whose `slotId` is not in this template.
   *
   * Never dropped, and surfaced rather than swallowed. This happens for real: a template
   * is edited, or a photo captured under an older build syncs down. Evidence outlives the
   * checklist that prompted it, so an unrecognised slot is a display problem, never a
   * reason to stop counting a photo that exists.
   */
  readonly unslotted: number;
}

/**
 * Compute what is still missing.
 *
 * Pure and synchronous so the UI can call it on every render without caching, and so it
 * is testable without a browser or a database.
 */
export function checklistProgress(
  template: InspectionTemplate,
  photos: readonly SlottedPhoto[],
): ChecklistProgress {
  const counts = new Map<string, number>();
  for (const photo of photos) {
    if (photo.redacted === true) continue;
    counts.set(photo.slotId, (counts.get(photo.slotId) ?? 0) + 1);
  }

  const slots: SlotProgress[] = template.slots.map((slot) => {
    const count = counts.get(slot.id) ?? 0;
    // Repeatable slots are satisfied by one photo: we cannot know how many slopes a house
    // has, and inventing a required count would nag a rep who is already done.
    return { slot, count, satisfied: count > 0 };
  });

  const known = new Set(template.slots.map((slot) => slot.id));
  let unslotted = 0;
  for (const [slotId, count] of counts) {
    if (!known.has(slotId)) unslotted += count;
  }

  const required = slots.filter((entry) => entry.slot.required);
  const missing = required.filter((entry) => !entry.satisfied).map((entry) => entry.slot);

  return {
    template,
    slots,
    requiredTotal: required.length,
    requiredSatisfied: required.length - missing.length,
    missing,
    complete: missing.length === 0,
    unslotted,
  };
}
